import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { motion } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import ContactForm from "../../components/ContactForm";
import CTAButton from "../../components/CTAButton";
import SEO from "../../components/SEO";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const faqs = [
  {
    question: "How long does post-construction cleaning take?",
    answer:
      "Typically 4-8 hours for residential properties and 1-3 days for commercial projects, depending on size and scope.",
  },
  {
    question: "Do you remove construction debris?",
    answer:
      "Yes, we remove all construction debris, including wood, nails, packaging materials, and other waste generated during construction.",
  },
  {
    question: "Can you clean newly painted surfaces?",
    answer:
      "Absolutely. We use specialised techniques and products safe for freshly painted walls and surfaces.",
  },
  {
    question: "Do I need to be present during cleaning?",
    answer:
      "Not necessarily. Many clients provide access and leave us to work. We'll call when finished for your inspection.",
  },
  {
    question: "What if I'm not satisfied?",
    answer:
      "We offer a 100% satisfaction guarantee. If you're not happy, we'll return to address any concerns at no extra cost.",
  },
];

const PostConstruction = () => {
  return (
    <>
      <SEO
        title="Post Construction Cleaning Services in Kampala | Professional Builders Clean"
        description="Expert post-construction cleaning in Kampala. Remove debris, dust & residue from renovation or new builds. Move-in ready guarantee. Trusted by 200+ construction sites. Call 0752 058 190"
        keywords="post construction cleaning Kampala, builders clean Uganda, after renovation cleaning, construction site cleaning, move-in cleaning Kampala, debris removal Uganda"
        url="https://blossomcleaningservices.com/services/post-construction"
      />
      <Header />
      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          py: 12,
          backgroundImage:
            "url(https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(34, 51, 95, 0.75)",
          },
        }}
      >
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              sx={{
                mb: 3,
                color: "white",
                fontSize: { xs: "2.5rem", md: "3.5rem" },
              }}
            >
              Post Construction Cleaning
            </Typography>
            <Typography
              variant="h5"
              sx={{ maxWidth: 800, color: "rgba(255,255,255,0.95)", mb: 4 }}
            >
              Transform your construction site into a pristine, move-in ready
              space
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <CTAButton to="/contact">Book Now</CTAButton>
              <CTAButton
                href="tel:0752058190"
                variant="outlined"
                sx={{ color: "white", borderColor: "white" }}
              >
                Call 0752 058 190
              </CTAButton>
            </Box>
          </motion.div>
        </Container>
      </Box>
      {/* Service Description */}
      <Box sx={{ py: 8 }}>
        <Container>
          <Grid container spacing={6}>
            <Grid item xs={12} md={8}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 600 }}>
                Professional Post-Construction Cleaning
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                Construction and renovation projects leave behind dust, debris,
                and residue that standard cleaning can't handle. Our
                post-construction cleaning service is designed specifically to
                tackle the unique challenges of newly built or renovated spaces.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
                We use industrial-grade equipment and specialised cleaning
                products to remove construction dust from every surface,
                including hard-to-reach areas. Our experienced team understands
                the nuances of post-construction cleaning, ensuring your space
                is not just clean, but truly move-in ready.
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
                Whether it's a new home, office renovation, or commercial build,
                we deliver meticulous cleaning that highlights your investment
                and creates a welcoming environment from day one.
              </Typography>

              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                What's Included
              </Typography>
              <Grid container spacing={2}>
                {[
                  "Complete dust removal from all surfaces",
                  "Window cleaning (inside and outside)",
                  "Floor cleaning and polishing",
                  "Debris and waste removal",
                  "Paint splatter and adhesive removal",
                  "Fixture and fitting cleaning",
                  "Cabinet and cupboard cleaning (inside and out)",
                  "Bathroom and kitchen deep clean",
                  "Light switch and outlet cleaning",
                  "Final inspection and touch-ups",
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <CheckCircleIcon
                        sx={{ color: "primary.main", fontSize: 20 }}
                      />
                      <Typography variant="body2">{item}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  mb: 4,
                  p: 3,
                  backgroundColor: "primary.main",
                  color: "white",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2, fontWeight: 600, color: "white" }}
                >
                  Quick Quote
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mb: 3, color: "rgba(255,255,255,0.9)" }}
                >
                  Get an instant estimate for your project
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <CTAButton
                    href="tel:0752058190"
                    variant="contained"
                    sx={{
                      backgroundColor: "white",
                      color: "primary.main",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.9)" },
                    }}
                  >
                    📞 Call Now
                  </CTAButton>
                  <CTAButton
                    to="/contact"
                    variant="outlined"
                    sx={{ color: "white", borderColor: "white" }}
                  >
                    Request Quote Online
                  </CTAButton>
                </Box>
              </Card>

              <Box
                component="img"
                src="https://images.unsplash.com/photo-1604014237800-1c9102c219da?w=800&q=80"
                alt="Construction cleaning"
                sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* Pricing */}/{/* FAQs */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            align="center"
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Frequently Asked Questions
          </Typography>
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>
      {/* Contact Form */}
      <Box sx={{ py: 8, backgroundColor: "background.paper" }}>
        <Container maxWidth="md">
          <ContactForm title="Request a Quote" showServiceSelect={false} />
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default PostConstruction;
